/* eslint-disable @typescript-eslint/consistent-type-assertions */

import type {
  BaseLocationRadiusConfig,
  DynamicTips,
  LocationPrimerVariation,
  MomoOutletIdMapping,
  OrderPaymentStateConfig,
  ProductDetailsCarouselVariation,
  StripePaymentMethodConfig,
  TargetTippingByLocation,
  UpsellPlacementVariation,
} from './defaults.types';

/**
 * Don't forget, the current naming convention is in the format of:
 * PROJECT-NUMBER-short-feature-enabled
 * PROJECT-NUMBER-permanent-feature-enabled
 * e.g. "XP-1234-short-ticket-name-enabled"
 * @see {@link https://sweetgreen.atlassian.net/wiki/spaces/TD/pages/4528570450/Feature+Flags#%F0%9F%93%9B-Naming-Conventions Naming Conventions}
 *
 * We track feature flag deletions in this spreadsheet:
 * @see {@link https://docs.google.com/spreadsheets/d/1gm_97S3nVXb2ptnaGkg7uCHRnZmkSr1XSnqG77Yj4ew/edit#gid=0 Feature Flag Removal}
 * Please create a ticket for removing short term feature flags that you created and include it there.
 */

export const featureFlagDefaults = {
  'permanent-forced-app-update-enabled': false,
  'permanent-order-crash-option-enabled': false,
  'permanent-contentful-preview-enabled': false,
  'permanent-feature-flag-overrides-enabled': false,
  'permanent-debug-menu-enabled': false,
  'permanent-sierra-enabled': false,
  'permanent-appsflyer-enabled': false,

  // !! ───────────────────────────────────────────────────────────────────────
  // !!                           W A R N I N G                              !!
  // !! ───────────────────────────────────────────────────────────────────────
  // !! Most flags should be `true` by default.                              !!
  // !! Flag that are `false` by default are very difficult to remove later. !!
  // !! Please consider if your flag should be `false` before committing it. !!
  // !! It should be reserved for flags that should be `false` if deleted.   !!
  // !! Usually this means special features that aren't always turned on.    !!
  // !! ───────────────────────────────────────────────────────────────────────

  'XP-3232-permanent-order-to-healthkit-enabled': true,
  'permanent-bag-wanted-times-polling-interval-milliseconds': 60_000,

  'OPS-enable-maintenance-mode': false,
  'OPS-contentful-enabled': true,
  'OPS-ca-data-request': true,

  'mnu-8339-display-redesign-ingredient-images': true,

  'CELS-1191-permanent-dynamic-tip-values': {} as DynamicTips,

  'CELS-1149-short-target-tipping-by-location-enabled': ['all'] as TargetTippingByLocation,
  'cels-2041-auth-hybrid-flow': false,
  'CELS-2107-auth-use-prefer-ephemeral-session': true,
  'CELS-997-live-activities-enabled': true,
  'CELS-3466-braze-live-activities-enabled': false,
  'cels-2138-challenge-opt-in-url-query-params-poc': true,
  'CELS-1449-in-app-gift-cards-max-schedule-days': 60,
  'CELS-2365-show-price-on-reorder-rail': true,
  'CELS-2359-delivery-fee-disclosure-banner': true,
  'CELS-2439-enable-delivery-price-differentiation-disclosure': true,
  'CELS-2489-enable-outpost-price-differentiation-disclosure': true,
  'CELS-2569-hide-price-on-favorites-and-share': true,
  'CELS-2917-reject-promo-code-previous-orders-enabled': true,
  'CELS-3094-menu-top-down-categories-enabled': true,
  'CELS-3031-sg-rewards-premium-enabled': false,
  'CELS-2957-home-offers-modal-enabled': true,
  'CELS-2957-home-in-progress-offers-modal-enabled': true,
  'CELS-3186-enable-zip-code-field': true,
  'CELS-3623-remote-dietary-restrictions-enabled': true,
  'CELS-4030-free-cyo-toppings-message-enabled': true,
  'cels-4044-enable-trademark': true,
  'CELS-4064-surface-distance-on-location-cards-enabled': true,
  'CELS-4212-too-far-location-threshold-in-miles': -1,
  'CELS-4290-promo-code-deep-link-enabled': true,
  'CELS-4127-order-playback-config': {},
  'CELS-4251-order-feedback-notifications-enabled': true,
  'CELS-4251-order-feedback-notification-delay': 30,
  'CELS-4258-forgot-to-scan-enabled': true,
  'CELS-4275-order-cancel-confirmation-modal-enabled': false,
  'CELS-4174-in-store-loyalty-orders-enabled': false,
  'CELS-4378-order-updated-tip-input-enabled': false,
  'CELS-4064-surface-prediction-distance-on-location-cards-enabled': false,
  'CELS-4323-order-macronutrients-enabled': true,
  'CELS-4432-upsell-modal-order': false,
  'permanent-order-upsell-number-of-products': 6,
  'CELS-4497-order-product-details-page-enabled': false,
  'CELS-4498-product-details-carousel-variation':
    'BASE_IMAGE_FIRST' as ProductDetailsCarouselVariation, // prettier-ignore
  'CELS-4617-order-search-delivery-locations-by-address-id-enabled': false,
  'CELS-4466-order-in-app-ota-install-enabled': false,
  'CELS-4466-order-in-app-ota-polling-interval': 60, // in minutes
  'cels-3985-usable-net-enabled': true,
  'cels-4626-momo-rate-order-survey-enabled': 'Disabled' as const,
  'cels-4651-momo-poc-restaurant-entity-id-mapping': {} as MomoOutletIdMapping,
  'cels-5364-momo-survey-all-digital-orders-enabled': false,
  'cels-4731-radar-pickup-autocomplete': false,
  'cels-4823-enable-radar-delivery-address-predictions': false,
  'CELS-4762-modification-group-helper-text-enabled': true,
  'CELS-4848-initial-order-channel-enabled': false,
  'CELS-5299-loyalty-redesign-enabled': false,
  'CELS-5381-order-is-brand-icon-enabled': false,
  'CELS-4859-base-location-radius-config': {
    SMALLEST: 3,
    SMALL: 7,
    MEDIUM: 15,
    LARGE: 40,
    XLARGE: 200,
  } satisfies BaseLocationRadiusConfig,
  'CELS-4828-gift-card-payment-state-polling-enabled': true,
  'CELS-4828-order-payment-state-polling-enabled': true,
  'CELS-4828-order-payment-state-config': {
    pollingInterval: 3000,
    expirationTolerance: 300_000,
    delayedTolerance: 12_000,
  } as OrderPaymentStateConfig,
  'CELS-5071-aws-waf-enabled': true,
  'cels-4664-enable-receipt-screen': false,
  'CELS-5135-allergen-compliance-enabled': false,
  'CELS-5341-order-delivery-minimum-block-enabled': false,
  'CELS-5095-order-reorder-rail-on-menu-enabled': false,
  'CELS-5018-radar-braze-integration-enabled': true,

  'permanent-stripe-payment-method-config': {
    defaultId: '',
    pollingDisabledId: '',
  } as StripePaymentMethodConfig,
  'CELS-4898-radar-geofence-enabled': false,

  'em-716-personalized-bowl-ux': true,
  'em-873-product-recommendation-card-variant': 'A',

  'MNU-9207-pdp-premium-top-display': false,
  'CELS-5031-detailed-line-item-modifications-enabled': false,
  'CELS-5026-customization-upsell-pricing-enabled': false,
  'CELS-5151-use-selected-modifiers-to-edit-line-item-enabled': false,
  'CELS-5355-order-group-ordering-enabled': false,
  'CELS-5406-order-cart-polling-interval': -1,
  'cels-5394-order-upsell-placement-experiment': 'control' as UpsellPlacementVariation,

  // ─── OTA Update ──────────────────────────────────────────────────────

  'OTA-update-in-app-checker-enabled': false,
  'OTA-update-in-app-checker-initial-check-timeout-ms': 10_000, // 10 seconds
  'OTA-update-in-app-checker-polling-interval-ms': 300_000, // 5 minutes
  'OTA-update-is-force-update-enabled': false,

  // ─── Notification Preferences ────────────────────────────────────────

  'CELS-3674-enable-notification-preference-center': false,
  'CELS-3514-allow-order-status-email-preferences': false,
  'CELS-4917-order-alternative-home-content-enabled': false,
  'CELS-5108-scan-to-pay-loyalty-entry-enabled': true,

  // ─── Location Permissions ────────────────────────────────────────────

  'CELS-5256-geofencing-primer-variant': 'disabled' as LocationPrimerVariation,
};
